"use client";

import { NoyaManager } from "@noya-app/state-manager";
import React, { ComponentProps } from "react";
import { createRoot } from "react-dom/client";

import { useIsomorphicLayoutEffect } from "@noya-app/react-utils";
import { Root } from "react-dom/client";
import { StateInspector } from "./StateInspector";

function createShadowRootElement() {
  const host = document.createElement("div");
  host.id = "noya-multiplayer-react-inspector-host";
  const shadowRoot = host.attachShadow({ mode: "open" });
  const container = document.createElement("div");
  shadowRoot.appendChild(container);
  return { host, container };
}

export function createStateInspectorRoot() {
  const { host, container } = createShadowRootElement();
  const root = createRoot(container);
  document.body.appendChild(host);

  return {
    root,
    unmount: () => {
      document.body.removeChild(host);
    },
  };
}

export function useStateInspector<
  S,
  M extends object,
  E extends object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>({
  noyaManager,
  disabled = false,
  advanced = false,
  colorScheme,
  anchor,
}: {
  noyaManager: NoyaManager<S, M, E, MenuT, I>;
  disabled?: boolean;
  advanced?: boolean;
  colorScheme?: ComponentProps<typeof StateInspector>["colorScheme"];
  anchor?: ComponentProps<typeof StateInspector>["anchor"];
}) {
  const [root, setRoot] = React.useState<Root | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (disabled) return;

    const { root, unmount } = createStateInspectorRoot();

    setRoot(root);

    return unmount;
  }, [disabled]);

  useIsomorphicLayoutEffect(() => {
    if (!root) return;

    root.render(
      <StateInspector
        colorScheme={colorScheme}
        anchor={anchor}
        noyaManager={noyaManager}
        advanced={advanced}
      />
    );
  }, [noyaManager, anchor, colorScheme, root, advanced]);
}
